import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  GetLatestAgentPulseUpdateResponseSchema,
  GetAgentPulseUpdatesResponseSchema,
  GetAgentPulseLeaderboardResponseSchema,
  GetAgentPulseLiveCountsResponseSchema,
  GetAgentPulseWonLostOpportunitiesResponseSchema,
  ViewsSummaryResponseSchema,
} from "@forgepoint/shared";
import type {
  AgentPulseUpdate,
  AgentPulseLeaderboardEntry,
  AgentPulseWonLostOpportunity,
  GetAgentPulseLiveCountsResponse,
  ViewsSummaryResponse,
} from "@forgepoint/shared";

const POLL_INTERVAL_MS = 30_000;

interface AgentPulseStore {
  latestUpdate: AgentPulseUpdate | null;
  historicalUpdates: AgentPulseUpdate[];
  leaderboardEntries: AgentPulseLeaderboardEntry[];
  wonLostOpportunities: AgentPulseWonLostOpportunity[];
  viewsSummary: ViewsSummaryResponse | null;
  liveCounts: GetAgentPulseLiveCountsResponse | null;
  isLoading: boolean;
  selectedPulseId: string | null;

  selectPulse: (id: string) => void;
  fetchAll: () => Promise<void>;
  fetchLatestUpdate: () => Promise<void>;
  fetchUpdates: () => Promise<void>;
  fetchLeaderboard: () => Promise<void>;
  fetchWonLostOpportunities: () => Promise<void>;
  fetchViewsSummary: () => Promise<void>;
  fetchLiveCounts: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

export const useAgentPulseStore = create<AgentPulseStore>((set) => ({
  latestUpdate: null,
  historicalUpdates: [],
  leaderboardEntries: [],
  wonLostOpportunities: [],
  viewsSummary: null,
  liveCounts: null,
  isLoading: true,
  selectedPulseId: null,

  /**
   * Selects the pulse week to scope the won/lost table and ticker to.
   * @param id - Id of the pulse update to select
   */
  selectPulse: (id: string) => {
    set({ selectedPulseId: id });
    useAgentPulseStore.getState().fetchWonLostOpportunities();
  },

  /**
   * Fetches all agent pulse data in parallel. Sets isLoading during the initial load.
   */
  fetchAll: async () => {
    set({ isLoading: true });
    const { selectedPulseId } = useAgentPulseStore.getState();

    try {
      const [
        latestRes,
        updatesRes,
        leaderboardRes,
        wonLostRes,
        viewsSummaryRes,
        liveCountsRes,
      ] = await Promise.all([
        fetchApi("agent-pulse/updates/latest", "GET"),
        fetchApi("agent-pulse/updates", "GET", {
          params: { limit: "20" },
        }),
        fetchApi("agent-pulse/leaderboard", "GET"),
        fetchApi("agent-pulse/won-lost-opportunities", "GET", {
          params: selectedPulseId ? { pulseId: selectedPulseId } : undefined,
        }),
        fetchApi("analytics/views/summary", "GET", {
          params: { weeks: "12", days: "30", limit: "5" },
        }),
        fetchApi("agent-pulse/live-counts", "GET"),
      ]);

      if (latestRes.ok) {
        const parsed = GetLatestAgentPulseUpdateResponseSchema.safeParse(
          latestRes.data,
        );
        if (parsed.success && parsed.data.update) {
          set({ latestUpdate: parsed.data.update });
        }
      }

      if (updatesRes.ok) {
        const parsed = GetAgentPulseUpdatesResponseSchema.safeParse(
          updatesRes.data,
        );
        if (parsed.success) {
          set({ historicalUpdates: parsed.data.updates });
        }
      }

      if (leaderboardRes.ok) {
        const parsed = GetAgentPulseLeaderboardResponseSchema.safeParse(
          leaderboardRes.data,
        );
        if (parsed.success) {
          set({ leaderboardEntries: parsed.data.entries });
        }
      }

      if (
        wonLostRes.ok &&
        useAgentPulseStore.getState().selectedPulseId === selectedPulseId
      ) {
        const parsed =
          GetAgentPulseWonLostOpportunitiesResponseSchema.safeParse(
            wonLostRes.data,
          );
        if (parsed.success) {
          set({ wonLostOpportunities: parsed.data.opportunities });
        }
      }

      if (viewsSummaryRes.ok) {
        const parsed = ViewsSummaryResponseSchema.safeParse(
          viewsSummaryRes.data,
        );
        if (parsed.success) {
          set({ viewsSummary: parsed.data });
        }
      }

      if (liveCountsRes.ok) {
        const parsed = GetAgentPulseLiveCountsResponseSchema.safeParse(
          liveCountsRes.data,
        );
        if (parsed.success) {
          set({ liveCounts: parsed.data });
        }
      }
    } catch (error) {
      captureException(error as Error);
    }

    set({ isLoading: false });
  },

  /**
   * Refetches only the latest agent pulse update.
   */
  fetchLatestUpdate: async () => {
    try {
      const res = await fetchApi("agent-pulse/updates/latest", "GET");
      if (res.ok) {
        const parsed = GetLatestAgentPulseUpdateResponseSchema.safeParse(
          res.data,
        );
        if (parsed.success && parsed.data.update) {
          set({ latestUpdate: parsed.data.update });
        }
      }
    } catch (error) {
      captureException(error as Error);
    }
  },

  /**
   * Refetches historical agent pulse updates.
   */
  fetchUpdates: async () => {
    try {
      const res = await fetchApi("agent-pulse/updates", "GET", {
        params: { limit: "20" },
      });
      if (res.ok) {
        const parsed = GetAgentPulseUpdatesResponseSchema.safeParse(res.data);
        if (parsed.success) {
          set({ historicalUpdates: parsed.data.updates });
        }
      }
    } catch (error) {
      captureException(error as Error);
    }
  },

  /**
   * Refetches the leaderboard entries.
   */
  fetchLeaderboard: async () => {
    try {
      const res = await fetchApi("agent-pulse/leaderboard", "GET");
      if (res.ok) {
        const parsed = GetAgentPulseLeaderboardResponseSchema.safeParse(
          res.data,
        );
        if (parsed.success) {
          set({ leaderboardEntries: parsed.data.entries });
        }
      }
    } catch (error) {
      captureException(error as Error);
    }
  },

  /**
   * Refetches won/lost opportunities for the wins table, scoped to the
   * selected pulse week. Discards the response if the selection changed
   * while the request was in flight.
   */
  fetchWonLostOpportunities: async () => {
    const { selectedPulseId } = useAgentPulseStore.getState();

    try {
      const res = await fetchApi("agent-pulse/won-lost-opportunities", "GET", {
        params: selectedPulseId ? { pulseId: selectedPulseId } : undefined,
      });
      if (
        res.ok &&
        useAgentPulseStore.getState().selectedPulseId === selectedPulseId
      ) {
        const parsed =
          GetAgentPulseWonLostOpportunitiesResponseSchema.safeParse(res.data);
        if (parsed.success) {
          set({ wonLostOpportunities: parsed.data.opportunities });
        }
      }
    } catch (error) {
      captureException(error as Error);
    }
  },

  /**
   * Refetches the views summary for SENT quotes (views per week and top cities).
   */
  fetchViewsSummary: async () => {
    try {
      const res = await fetchApi("analytics/views/summary", "GET", {
        params: { weeks: "12", days: "30", limit: "5" },
      });
      if (res.ok) {
        const parsed = ViewsSummaryResponseSchema.safeParse(res.data);
        if (parsed.success) {
          set({ viewsSummary: parsed.data });
        }
      }
    } catch (error) {
      captureException(error as Error);
    }
  },

  /**
   * Refetches live counts of opportunities and quotes since the last pulse.
   */
  fetchLiveCounts: async () => {
    try {
      const res = await fetchApi("agent-pulse/live-counts", "GET");
      if (res.ok) {
        const parsed = GetAgentPulseLiveCountsResponseSchema.safeParse(
          res.data,
        );
        if (parsed.success) {
          set({ liveCounts: parsed.data });
        }
      }
    } catch (error) {
      captureException(error as Error);
    }
  },

  /**
   * Starts polling all agent pulse data on a fixed interval.
   * Calls fetchAll without setting isLoading to avoid flashing skeletons.
   */
  startPolling: () => {
    const { stopPolling, fetchAll } = useAgentPulseStore.getState();
    stopPolling();

    const poll = async () => {
      const store = useAgentPulseStore.getState();
      await Promise.all([
        store.fetchLatestUpdate(),
        store.fetchUpdates(),
        store.fetchLeaderboard(),
        store.fetchWonLostOpportunities(),
        store.fetchViewsSummary(),
        store.fetchLiveCounts(),
      ]);
    };

    pollTimerId = window.setInterval(poll, POLL_INTERVAL_MS);

    // Also do the initial full load
    fetchAll();
  },

  /**
   * Stops the polling interval if one is running.
   */
  stopPolling: () => {
    if (pollTimerId !== null) {
      window.clearInterval(pollTimerId);
      pollTimerId = null;
    }
  },
}));

let pollTimerId: number | null = null;
